import { analytics } from '@/services/Analytics'
import { logger } from '@/utils/logging'
import { getStringFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage'

/**
 * Gets the current analytics session ID
 * This session ID is automatically generated by the analytics library and persists for the browser session
 * Falls back to sessionStorage if the analytics library hasn't fully initialized yet
 *
 * @returns The session ID string, or undefined if analytics is not initialized or on server
 */
export const getAnalyticsSessionId = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const { context } = analytics.getState()
    const sessionId = context?.sessionId

    if (sessionId) {
      // Store in sessionStorage for fallback
      writeToSessionStorage('analyticsSessionId', sessionId)
      return sessionId
    }

    // Fallback to sessionStorage if analytics hasn't initialized yet
    const cachedSessionId = getStringFromSessionStorage('analyticsSessionId')
    if (cachedSessionId) {
      logger().debug('Using cached analytics sessionId from sessionStorage', { cachedSessionId })
      return cachedSessionId
    }

    logger().debug('Analytics sessionId is not available', { context })
    return undefined
  } catch (error) {
    logger().error('Error getting analytics session ID', { error })

    // Try sessionStorage fallback even on error
    const cachedSessionId = getStringFromSessionStorage('analyticsSessionId')
    if (cachedSessionId) {
      logger().debug('Using cached analytics sessionId from sessionStorage after error', { cachedSessionId })
      return cachedSessionId
    }

    return undefined
  }
}

/**
 * Gets the current analytics locale from the browser context
 * This returns the full locale format (e.g., 'en-US', 'fr-CA', 'fr-FR')
 * as detected by the analytics library from navigator.language
 *
 * @returns The locale string (e.g., 'en-US'), or undefined if analytics is not initialized or on server
 */
export const getAnalyticsLocale = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const { context } = analytics.getState()
    const locale = context?.locale

    if (locale) {
      return locale
    }

    logger().debug('Analytics locale is not available', { context })
    return undefined
  } catch (error) {
    logger().error('Error getting analytics locale', {}, error)
    return undefined
  }
}

/**
 * Gets headers with session ID included
 * Useful for adding session tracking to HTTP requests
 *
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with X-SESSION-ID if session is available
 */
export const getHeadersWithSessionId = (additionalHeaders?: Record<string, string>): Record<string, string> => {
  const sessionId = getAnalyticsSessionId()
  return {
    ...additionalHeaders,
    ...(sessionId ? { 'X-SESSION-ID': sessionId } : {}),
  }
}
